import { GatewayQuestionnaire } from "@conformite/gateway";
import {
  Table,
  Button,
  displayErrorToast,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import { PageWithTitleTemplate } from "@src/components/templates/PageWithTitleTemplate";
import { QuestionnaireApi } from "@src/data/questionnaire/questionnaire.api";
import { useQuestionnaireDefinitionsQuery } from "@src/data/questionnaire/hook/useQuestionnaireDefinitionsQuery";
import { questionnaireKeys } from "@src/data/questionnaire/hook/questionnaire.keys";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FIL_ARIANE_MES_CONTROLES } from "@src/pages/controle-quinquennal/cqListe.wording";
import styles from "./QuestionnaireAdminListPage.module.scss";

type ExportedDefinition =
  GatewayQuestionnaire.CreateQuestionnaireDefinitionRequestType;

export function QuestionnaireAdminListPage() {
  const { data: definitions, isLoading } = useQuestionnaireDefinitionsQuery();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);

  const handleExport = () => {
    if (!definitions?.length) return;
    const exportData: ExportedDefinition[] = definitions.map((def) => ({
      slug: def.slug,
      name: def.name,
      description: def.description ?? undefined,
      questions: [...def.questions]
        .sort((a, b) => a.order - b.order)
        .map((q) => ({
          id: q.id,
          label: q.label,
          helpText: q.helpText ?? undefined,
          indicationIA: q.indicationIA ?? "",
          order: q.order,
          options: q.options,
          required: q.required,
          condition: q.condition ?? undefined,
          type: q.type,
        })),
    }));
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "questionnaires.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      displayErrorToast("Fichier invalide — impossible de lire le JSON");
      return;
    }

    if (!Array.isArray(parsed) || parsed.length === 0) {
      displayErrorToast(
        "Format invalide — le fichier doit contenir un tableau de questionnaires"
      );
      return;
    }

    setIsImporting(true);
    const errors: string[] = [];
    const existingBySlug = new Map(definitions?.map((d) => [d.slug, d]) ?? []);

    // Import séquentiel volontaire : upsert un par un pour collecter les erreurs
    // par élément. Slug déjà présent → mise à jour, sinon création.
    // eslint-disable-next-line no-restricted-syntax
    for (const def of parsed as ExportedDefinition[]) {
      try {
        const existing = existingBySlug.get(def.slug);
        if (existing) {
          // eslint-disable-next-line no-await-in-loop
          await QuestionnaireApi.updateDefinition(existing.id, {
            name: def.name,
            description: def.description,
            questions: def.questions,
          });
        } else {
          // eslint-disable-next-line no-await-in-loop
          await QuestionnaireApi.createDefinition(def);
        }
      } catch {
        errors.push(def.slug ?? "?");
      }
    }

    await queryClient.invalidateQueries({
      queryKey: questionnaireKeys.definitions(),
    });
    setIsImporting(false);

    if (errors.length === 0) {
      displaySucessToast(
        `${parsed.length} questionnaire(s) importé(s) avec succès`
      );
    } else {
      displayErrorToast(
        `Échec pour : ${errors.join(", ")} (données invalides ou erreur serveur)`
      );
    }
  };

  const breadcrumbElements = [
    FIL_ARIANE_MES_CONTROLES,
    { label: "Administration des formulaires", value: "current" },
  ];

  return (
    <PageWithTitleTemplate
      title="Administration des formulaires"
      breadcrumb={{
        items: breadcrumbElements,
        setSelected: (nextRoute) => {
          if (nextRoute !== "current") navigate(nextRoute);
        },
        selected: "current",
      }}
      actions={
        <div className={styles.headerActions}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className={styles.hiddenFileInput}
            onChange={(e) => {
              void handleImport(e);
            }}
          />
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={!definitions?.length}
          >
            Exporter
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
          >
            {isImporting ? "Importation…" : "Importer"}
          </Button>
          <Button
            variant="filled"
            onClick={() => navigate("/admin/questionnaires/new")}
          >
            Créer un questionnaire
          </Button>
        </div>
      }
    >
      <div className={styles.container}>
        <Table<GatewayQuestionnaire.QuestionnaireDefinitionResponse>
          data={definitions ?? []}
          textNoData={isLoading ? "Chargement..." : "Aucun questionnaire"}
          onRowClick={(row) => navigate(`/admin/questionnaires/${row.slug}`)}
          columns={[
            {
              key: "name",
              title: "Nom",
              size: 8,
              render: ({ name }) => <p>{name}</p>,
            },
            {
              key: "slug",
              title: "Slug",
              size: 6,
              render: ({ slug }) => <p className={styles.slug}>{slug}</p>,
            },
            {
              key: "version",
              title: "Version",
              size: 3,
              render: ({ version }) => <p>v{version}</p>,
            },
            {
              key: "questions",
              title: "Questions",
              size: 3,
              render: ({ questions }) => <p>{questions.length}</p>,
            },
          ]}
        />
      </div>
    </PageWithTitleTemplate>
  );
}
